// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/intakeUnsavedLeaveGuard.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

export type IntakeLeaveProceed = () => void;

export type IntakeUnsavedLeaveGuardApi = {
  isDirty: () => boolean;
  requestLeave: (proceed: IntakeLeaveProceed) => void;
};

type IntakeUnsavedLeaveGuardContextValue = {
  registerGuard: (api: IntakeUnsavedLeaveGuardApi | null) => void;
  /**
   * If Intake is dirty, opens the leave dialog and returns false.
   * Otherwise runs proceed immediately and returns true.
   */
  tryNavigateAway: (proceed: IntakeLeaveProceed) => boolean;
};

const IntakeUnsavedLeaveGuardContext =
  createContext<IntakeUnsavedLeaveGuardContextValue | null>(null);

export function IntakeUnsavedLeaveGuardProvider({
  children,
}: {
  children: ReactNode;
}) {
  const guardRef = useRef<IntakeUnsavedLeaveGuardApi | null>(null);

  const registerGuard = useCallback((api: IntakeUnsavedLeaveGuardApi | null) => {
    guardRef.current = api;
  }, []);

  const tryNavigateAway = useCallback((proceed: IntakeLeaveProceed) => {
    const guard = guardRef.current;
    if (!guard || !guard.isDirty()) {
      proceed();
      return true;
    }

    guard.requestLeave(proceed);
    return false;
  }, []);

  const value = useMemo(
    () => ({ registerGuard, tryNavigateAway }),
    [registerGuard, tryNavigateAway]
  );

  return (
    <IntakeUnsavedLeaveGuardContext.Provider value={value}>
      {children}
    </IntakeUnsavedLeaveGuardContext.Provider>
  );
}

export function useRegisterIntakeUnsavedLeaveGuard() {
  return useContext(IntakeUnsavedLeaveGuardContext)?.registerGuard ?? null;
}

export function useTryNavigateAwayWithIntakeGuard() {
  const context = useContext(IntakeUnsavedLeaveGuardContext);
  return (
    context?.tryNavigateAway ??
    ((proceed: IntakeLeaveProceed) => {
      proceed();
      return true;
    })
  );
}
